import type { ActivityContext, ActivityHandle } from '../../core/activity';
import { makeDraggable } from '../../shared/drag-drop';
import levelsData from './levels.json';

interface FixedSubLevel {
  instruction?: string;
  randomisedInputData: false;
  totalBoys: number;
  totalGirls: number;
  totalCandies: number;
  forceShowBasket: boolean;
  placedInBoys: number;
  placedInGirls: number;
}

interface RandomSubLevel {
  randomisedInputData: true;
  maxBoys: number;
  maxGirls: number;
  maxCandies: number;
  alreadyPlaced: boolean;
}

type SubLevelRaw = FixedSubLevel | RandomSubLevel;

interface DataEntry {
  shuffle: boolean;
  subLevels: SubLevelRaw[];
}

interface Level {
  level: number;
  objective: string;
  difficulty: number;
  data: DataEntry[];
}

interface LevelsFile {
  levels: Level[];
}

interface ResolvedSubLevel {
  totalBoys: number;
  totalGirls: number;
  totalCandies: number;
  forceShowBasket: boolean;
  placedInBoys: number;
  placedInGirls: number;
}

const LEVELS = (levelsData as LevelsFile).levels;

function randInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function resolveSubLevel(raw: SubLevelRaw): ResolvedSubLevel {
  if (!raw.randomisedInputData) {
    return {
      totalBoys: raw.totalBoys,
      totalGirls: raw.totalGirls,
      totalCandies: raw.totalCandies,
      forceShowBasket: raw.forceShowBasket,
      placedInBoys: raw.placedInBoys,
      placedInGirls: raw.placedInGirls,
    };
  }
  const totalBoys = randInt(0, raw.maxBoys);
  const totalGirls = Math.max(1, randInt(0, raw.maxGirls));
  const numKids = totalBoys + totalGirls;
  const totalCandies = Math.max(numKids, randInt(Math.floor(raw.maxCandies / 2), raw.maxCandies));
  let placedInBoys = 0;
  let placedInGirls = 0;
  if (raw.alreadyPlaced) {
    const targetPerKid = Math.floor(totalCandies / numKids);
    if (targetPerKid > 0) {
      if (totalBoys > 0 && Math.random() < 0.5) placedInBoys = randInt(0, Math.max(0, targetPerKid - 1));
      if (totalGirls > 0 && (placedInBoys === 0 || Math.random() < 0.5)) {
        placedInGirls = randInt(0, Math.max(0, targetPerKid - 1));
      }
    }
  }
  return {
    totalBoys,
    totalGirls,
    totalCandies,
    forceShowBasket: true,
    placedInBoys,
    placedInGirls,
  };
}

function flatten(level: Level): SubLevelRaw[] {
  const out: SubLevelRaw[] = [];
  for (const entry of level.data) out.push(...entry.subLevels);
  return out;
}

function describeKids(boys: number, girls: number): string {
  if (boys === 0 && girls === 1) return '1 menina';
  if (boys === 0 && girls > 1) return `${girls} meninas`;
  if (girls === 0 && boys === 1) return '1 menino';
  if (girls === 0 && boys > 1) return `${boys} meninos`;
  const b = boys === 1 ? '1 menino' : `${boys} meninos`;
  const g = girls === 1 ? '1 menina' : `${girls} meninas`;
  return `${b} e ${g}`;
}

function buildInstructionPt(cfg: ResolvedSubLevel): string {
  const numKids = cfg.totalBoys + cfg.totalGirls;
  const totalToShare = cfg.totalCandies - cfg.placedInBoys * cfg.totalBoys - cfg.placedInGirls * cfg.totalGirls;
  const kidsDesc = describeKids(cfg.totalBoys, cfg.totalGirls);
  const willHaveRest = (cfg.totalCandies % numKids) !== 0;
  let s = `Distribua <strong>${totalToShare} doces</strong> entre <strong>${kidsDesc}</strong>`;
  if (cfg.placedInBoys > 0 || cfg.placedInGirls > 0) {
    const parts: string[] = [];
    if (cfg.placedInBoys > 0) parts.push(`os meninos já têm ${cfg.placedInBoys} cada`);
    if (cfg.placedInGirls > 0) parts.push(`as meninas já têm ${cfg.placedInGirls} cada`);
    s += `. <em>${parts.join(' e ')}</em>`;
  }
  s += '.';
  if (willHaveRest) s += ' O que sobrar vai pra cesta 🧺.';
  else s += ' Todos devem receber a mesma quantidade.';
  return s;
}

export function mountShare(ctx: ActivityContext): ActivityHandle {
  const level = LEVELS.find((l) => l.level === ctx.level) ?? LEVELS[0];
  if (!level) {
    ctx.root.innerHTML = '<p>Nível não encontrado.</p>';
    return { unmount: () => { ctx.root.innerHTML = ''; } };
  }

  const flat = flatten(level);
  const totalSubs = flat.length;

  let subIndex = 0;
  let correctCount = 0;
  let cfg: ResolvedSubLevel = resolveSubLevel(flat[0]!);
  let addedToGirls: number[] = [];
  let addedToBoys: number[] = [];
  let inBasket = 0;
  let buttonsBlocked = false;

  const page = document.createElement('div');
  page.className = 'activity-page';
  page.innerHTML = `
    <header class="activity-bar">
      <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
      <div class="activity-bar__title">Dividir doces</div>
      <div class="activity-bar__level">Nível ${level.level} de ${LEVELS.length}</div>
    </header>
    <section class="activity-stage share">
      <div class="feedback" aria-live="polite"></div>
      <p class="share__progress" aria-live="polite"></p>
      <div class="share__instruction" aria-live="polite"></div>
      <div class="share__children" role="list"></div>
      <div class="share__pool-row"></div>
      <div class="share__actions">
        <button class="btn btn--secondary share__reset" type="button">↺ Reiniciar</button>
        <button class="btn btn--primary share__confirm" type="button">Confirmar</button>
      </div>
    </section>
  `;
  ctx.root.appendChild(page);

  const progressEl = page.querySelector<HTMLElement>('.share__progress')!;
  const instructionEl = page.querySelector<HTMLElement>('.share__instruction')!;
  const childrenEl = page.querySelector<HTMLElement>('.share__children')!;
  const poolRowEl = page.querySelector<HTMLElement>('.share__pool-row')!;
  const feedback = page.querySelector<HTMLElement>('.feedback')!;
  const resetBtn = page.querySelector<HTMLButtonElement>('.share__reset')!;
  const confirmBtn = page.querySelector<HTMLButtonElement>('.share__confirm')!;
  page.querySelector<HTMLElement>('.activity-bar__back')!.addEventListener('click', () => ctx.onExit());

  resetBtn.addEventListener('click', () => {
    if (buttonsBlocked) return;
    addedToBoys = new Array(cfg.totalBoys).fill(0);
    addedToGirls = new Array(cfg.totalGirls).fill(0);
    inBasket = 0;
    ctx.audio.play('click');
    renderAll();
  });
  confirmBtn.addEventListener('click', validate);

  function startSubLevel(): void {
    cfg = resolveSubLevel(flat[subIndex]!);
    addedToBoys = new Array(cfg.totalBoys).fill(0);
    addedToGirls = new Array(cfg.totalGirls).fill(0);
    inBasket = 0;
    buttonsBlocked = false;
    renderAll();
  }

  function poolRemaining(): number {
    const distributedByPlayer = addedToBoys.reduce((a, b) => a + b, 0) + addedToGirls.reduce((a, b) => a + b, 0);
    const alreadyPlaced = cfg.placedInBoys * cfg.totalBoys + cfg.placedInGirls * cfg.totalGirls;
    return cfg.totalCandies - alreadyPlaced - distributedByPlayer - inBasket;
  }

  function showBasket(): boolean {
    if (cfg.forceShowBasket) return true;
    const numKids = cfg.totalBoys + cfg.totalGirls;
    return cfg.totalCandies % numKids !== 0;
  }

  function renderAll(): void {
    renderProgress();
    renderInstruction();
    renderChildren();
    renderPoolRow();
  }

  function renderProgress(): void {
    progressEl.textContent = `Pergunta ${subIndex + 1} de ${totalSubs}`;
  }

  function renderInstruction(): void {
    instructionEl.innerHTML = buildInstructionPt(cfg);
  }

  function renderChildren(): void {
    childrenEl.innerHTML = '';
    for (let i = 0; i < cfg.totalGirls; i++) {
      childrenEl.appendChild(makeChild('girl', i));
    }
    for (let i = 0; i < cfg.totalBoys; i++) {
      childrenEl.appendChild(makeChild('boy', i));
    }
  }

  function makeChild(kind: 'girl' | 'boy', idx: number): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = `share__child share__child--${kind}`;
    const placed = kind === 'girl' ? cfg.placedInGirls : cfg.placedInBoys;
    const added = (kind === 'girl' ? addedToGirls[idx] : addedToBoys[idx]) ?? 0;
    const total = placed + added;
    const emoji = document.createElement('div');
    emoji.className = 'share__child-emoji';
    emoji.textContent = kind === 'girl' ? '👧' : '🧒';
    const count = document.createElement('div');
    count.className = `share__child-count ${total > 0 ? 'share__child-count--has' : ''}`;
    count.textContent = total === 0 ? '—' : '🍬'.repeat(Math.min(total, 6)) + (total > 6 ? `×${total}` : '');
    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'share__child-remove';
    removeBtn.textContent = '−1';
    removeBtn.disabled = buttonsBlocked || added === 0;
    removeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (buttonsBlocked) return;
      if (kind === 'girl') addedToGirls[idx] = Math.max(0, (addedToGirls[idx] ?? 0) - 1);
      else addedToBoys[idx] = Math.max(0, (addedToBoys[idx] ?? 0) - 1);
      ctx.audio.play('click');
      renderAll();
    });
    wrap.appendChild(emoji);
    wrap.appendChild(count);
    wrap.appendChild(removeBtn);
    wrap.dataset['kind'] = kind;
    wrap.dataset['childIdx'] = String(idx);
    wrap.classList.add('share__drop-target');
    wrap.addEventListener('click', () => {
      if (buttonsBlocked) return;
      if (poolRemaining() <= 0) return;
      if (kind === 'girl') addedToGirls[idx] = (addedToGirls[idx] ?? 0) + 1;
      else addedToBoys[idx] = (addedToBoys[idx] ?? 0) + 1;
      ctx.audio.play('click');
      renderAll();
    });
    return wrap;
  }

  function addToChild(kind: 'girl' | 'boy', idx: number): void {
    if (buttonsBlocked) return;
    if (poolRemaining() <= 0) return;
    if (kind === 'girl') addedToGirls[idx] = (addedToGirls[idx] ?? 0) + 1;
    else addedToBoys[idx] = (addedToBoys[idx] ?? 0) + 1;
    ctx.audio.play('click');
    renderAll();
  }

  function addToBasket(): void {
    if (buttonsBlocked) return;
    if (poolRemaining() <= 0) return;
    inBasket++;
    ctx.audio.play('click');
    renderAll();
  }

  function renderPoolRow(): void {
    poolRowEl.innerHTML = '';
    const pool = document.createElement('div');
    pool.className = 'share__pool';
    pool.innerHTML = `
      <div class="share__pool-emoji">🍬</div>
      <div class="share__pool-count">${poolRemaining()}</div>
      <div class="share__pool-label">no monte (arraste pra distribuir)</div>
    `;
    poolRowEl.appendChild(pool);
    if (poolRemaining() > 0) {
      makeDraggable(pool, {
        getDropTarget: (x, y) => {
          const e = document.elementFromPoint(x, y);
          return e?.closest<HTMLElement>('[data-kind], [data-basket]') ?? null;
        },
        onDrop: (targetEl) => {
          if (targetEl.dataset['basket'] === '1') {
            addToBasket();
            return;
          }
          const kind = targetEl.dataset['kind'] as 'girl' | 'boy' | undefined;
          const idx = parseInt(targetEl.dataset['childIdx'] ?? '-1', 10);
          if (!kind || idx < 0) return;
          addToChild(kind, idx);
        },
      });
    }
    if (showBasket()) {
      const basket = document.createElement('div');
      basket.className = 'share__basket';
      basket.setAttribute('role', 'button');
      basket.dataset['basket'] = '1';
      basket.innerHTML = `
        <div class="share__basket-emoji">🧺</div>
        <div class="share__basket-count">${inBasket}</div>
        <div class="share__basket-label">cesta (sobra)</div>
      `;
      basket.addEventListener('click', () => {
        if (buttonsBlocked) return;
        if (poolRemaining() <= 0) {
          if (inBasket > 0) {
            inBasket--;
            ctx.audio.play('click');
            renderAll();
          }
          return;
        }
        addToBasket();
      });
      poolRowEl.appendChild(basket);
    }
  }

  function showFeedback(kind: 'ok' | 'err'): void {
    feedback.textContent = kind === 'ok' ? '✓' : '✗';
    feedback.className = `feedback feedback--show feedback--${kind}`;
    window.setTimeout(() => feedback.classList.remove('feedback--show'), 500);
  }

  function validate(): void {
    if (buttonsBlocked) return;
    const numKids = cfg.totalBoys + cfg.totalGirls;
    const expectedPerKid = Math.floor(cfg.totalCandies / numKids);
    const expectedRest = cfg.totalCandies % numKids;
    let allOk = true;
    for (let i = 0; i < cfg.totalBoys; i++) {
      if (cfg.placedInBoys + (addedToBoys[i] ?? 0) !== expectedPerKid) { allOk = false; break; }
    }
    if (allOk) {
      for (let i = 0; i < cfg.totalGirls; i++) {
        if (cfg.placedInGirls + (addedToGirls[i] ?? 0) !== expectedPerKid) { allOk = false; break; }
      }
    }
    if (allOk && inBasket !== expectedRest) allOk = false;
    if (allOk && poolRemaining() !== 0) allOk = false;
    if (allOk) {
      buttonsBlocked = true;
      ctx.audio.play('correct');
      showFeedback('ok');
      correctCount++;
      subIndex++;
      window.setTimeout(() => {
        if (subIndex < totalSubs) startSubLevel();
        else finishLevel();
      }, 700);
    } else {
      ctx.audio.play('wrong');
      showFeedback('err');
    }
  }

  function finishLevel(): void {
    ctx.audio.play('win');
    ctx.onComplete(level.level, correctCount);
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    stage.innerHTML = `
      <h2 class="activity-finish__title">Nível ${level.level} concluído!</h2>
      <p class="activity-finish__text">Você dividiu ${correctCount} de ${totalSubs} montes de doces.</p>
      <div class="activity-finish__actions">
        <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
        ${level.level < LEVELS.length
          ? `<button class="btn btn--primary" type="button" data-act="next">Próximo nível</button>`
          : ''}
      </div>
    `;
    stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
    const next = stage.querySelector<HTMLElement>('[data-act="next"]');
    if (next) {
      next.addEventListener('click', () => {
        window.location.hash = `/share/${level.level + 1}`;
      });
    }
  }

  startSubLevel();

  return {
    unmount(): void {
      ctx.root.innerHTML = '';
    },
  };
}
